import { useState, useMemo } from "react";
import { ClipboardList, RefreshCw, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OrderFilters } from "@/components/orders/OrderFilters";
import { OrdersTable } from "@/components/orders/OrdersTable";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useOrdersList } from "@/hooks/useOrdersList";
import type { Database } from "@/integrations/supabase/types";

type OrderStatus = Database["public"]["Enums"]["order_status"];

export default function PedidosPage() {
  const [status, setStatus] = useState<OrderStatus | "all">("all");
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [page, setPage] = useState(1);

  const {
    orders,
    totalOrders,
    totalPages,
    isLoading,
    refetch,
    updateStatus,
    cancelOrder,
    isUpdating,
  } = useOrdersList({
    status,
    dateFrom,
    dateTo,
    page,
    pageSize: 20,
  });

  // Count pending orders for the alert
  const pendingOrders = useMemo(() => 
    orders.filter(o => o.status === "pending"), 
    [orders]
  );

  const handleClearFilters = () => {
    setStatus("all");
    setDateFrom(undefined);
    setDateTo(undefined);
    setPage(1);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <ClipboardList className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Pedidos</h1>
            <p className="text-sm text-muted-foreground">
              {totalOrders} pedido(s) encontrado(s)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {pendingOrders.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 animate-pulse">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
                {pendingOrders.length} pendente{pendingOrders.length > 1 ? "s" : ""}
              </span>
              <Badge variant="outline" className="bg-amber-500 text-white border-0">
                Aguardando
              </Badge>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <OrderFilters
          status={status}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onStatusChange={(s) => {
            setStatus(s);
            setPage(1);
          }}
          onDateFromChange={(d) => {
            setDateFrom(d);
            setPage(1);
          }}
          onDateToChange={(d) => {
            setDateTo(d);
            setPage(1);
          }}
          onClearFilters={handleClearFilters}
        />

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <OrdersTable
            orders={orders}
            onUpdateStatus={updateStatus}
            onCancelOrder={cancelOrder}
            isUpdating={isUpdating}
          />
        )}

        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      onClick={() => setPage(pageNum)}
                      isActive={page === pageNum}
                      className="cursor-pointer"
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className={
                    page === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </div>
  );
}
